import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Box, Grid, Typography, List, ListItem, ListItemButton,
  ListItemIcon, ListItemText, CircularProgress,
} from "@mui/material";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import LockIcon from "@mui/icons-material/Lock";

import Loading from "../../components/Loading";
import { PaymentService } from "../../services/PaymentService";
import { PAYMENT_TYPE_INFO } from "../../constants/paymentMaps";
import UpiSection from "./sections/UpiSection";
import CardSection from "./sections/CardSection";
import NetBankingSection from "./sections/NetBankingSection";
import WalletSection from "./sections/WalletSection";
import "./CheckoutPage.css";

// ─────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────
const SESSION_DURATION = 15 * 60;   // 15 minutes in seconds
const SESSION_KEY      = "ckoSessionStartedAt";
const TERMINAL_STATUSES = ["SUCCESS", "FAILED", "CANCELLED"];

const formatTime = (s) =>
  `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

// ─────────────────────────────────────────────────────────────────
// Sidebar item
// ─────────────────────────────────────────────────────────────────
const PaymentMethodItem = ({ entry, selected, onClick }) => {
  const info = PAYMENT_TYPE_INFO[entry.paymentType] ?? { label: entry.paymentType, iconType: null };

  const icon = (() => {
    switch (info.iconType) {
      case "upi":    return <Box component="img" src="/images/bhim.png" alt="UPI" sx={{ width: 26, height: 26, objectFit: "contain" }} />;
      case "card":   return <CreditCardIcon />;
      case "bank":   return <AccountBalanceIcon />;
      case "wallet": return <AccountBalanceWalletIcon />;
      default:       return <CreditCardIcon />;
    }
  })();

  return (
    <ListItem disablePadding sx={{ mb: 0.5 }}>
      <ListItemButton
        selected={selected}
        onClick={() => onClick(entry)}
        sx={{
          borderRadius: "10px",
          py: 1.5, px: 2,
          transition: "all 0.2s ease",
          background: selected ? "#fff" : "transparent",
          border: `1px solid ${selected ? "#15b86d" : "transparent"}`,
          "&:hover:not(.Mui-selected)": { background: "#f9fafb", border: "1px solid #e5e7eb" },
          "&.Mui-selected": { background: "#fff", color: "#15b86d" },
          "&.Mui-selected .MuiListItemIcon-root": { color: "#15b86d" },
        }}
      >
        <ListItemIcon sx={{ minWidth: 40, color: selected ? "#15b86d" : "#6b7280", transition: "color 0.2s" }}>
          {icon}
        </ListItemIcon>
        <ListItemText
          primary={info.label}
          primaryTypographyProps={{
            fontSize: "0.93rem",
            fontWeight: selected ? 600 : 500,
            color: selected ? "#1f2937" : "#374151",
          }}
        />
      </ListItemButton>
    </ListItem>
  );
};

// ─────────────────────────────────────────────────────────────────
// Payment Waiting Overlay  (covers entire screen)
// ─────────────────────────────────────────────────────────────────
const OVERLAY_CONFIG = {
  collect:    { title: "Payment Request Sent",  message: "Please approve the request in your UPI app", showTimer: true  },
  intent:     { title: "Waiting for Payment",   message: "Complete the payment in your UPI app and return here", showTimer: false },
  card:       { title: "Complete Your Payment", message: "A payment window was opened — please complete payment there", showTimer: false },
  netbanking: { title: "Complete Your Payment", message: "A payment window was opened — please complete payment there", showTimer: false },
  wallet:     { title: "Complete Your Payment", message: "A payment window was opened — please complete payment there", showTimer: false },
};

const PaymentWaitingOverlay = ({ mode, timer }) => {
  const { title, message, showTimer } = OVERLAY_CONFIG[mode] ?? { title: "Processing…", message: "Please wait", showTimer: false };
  return (
    <Box sx={{
      position: "fixed", inset: 0, zIndex: 3000,
      background: "rgba(15,23,42,0.65)",
      backdropFilter: "blur(4px)",
      display: "flex", alignItems: "center", justifyContent: "center", p: 2,
    }}>
      <Box sx={{
        background: "#fff", borderRadius: "20px",
        p: { xs: 3, sm: 4 }, maxWidth: 380, width: "100%",
        textAlign: "center",
        boxShadow: "0 24px 64px rgba(0,0,0,0.3)",
        animation: "slideUp 0.35s ease-out",
      }}>
        {/* Spinner + lock icon stacked */}
        <Box sx={{ mb: 2.5, position: "relative", display: "inline-flex" }}>
          <CircularProgress size={68} thickness={2.5} sx={{ color: "#15b86d" }} />
          <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <LockIcon sx={{ color: "#15b86d", fontSize: "1.6rem" }} />
          </Box>
        </Box>

        <Typography variant="h6" fontWeight={700} color="#1f2937" mb={1}>{title}</Typography>
        <Typography variant="body2" color="#6b7280" mb={2}>{message}</Typography>

        {showTimer && (
          <Box sx={{ background: "#f0fdf4", borderRadius: "12px", py: 1.5, mb: 2 }}>
            <Typography variant="h4" fontWeight={800} color="#15b86d">{formatTime(timer)}</Typography>
            <Typography variant="caption" color="#6b7280">Time remaining</Typography>
          </Box>
        )}

        <Box display="flex" alignItems="center" justifyContent="center" gap={0.75}>
          <CircularProgress size={10} sx={{ color: "#9ca3af" }} />
          <Typography variant="caption" color="#9ca3af">Checking payment status…</Typography>
        </Box>
      </Box>
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────
// Main CheckoutPage
// ─────────────────────────────────────────────────────────────────
const CheckoutPage = () => {
  const id      = useParams()["*"];   // Full path after /pay/ — handles Base64 IDs that contain "/"
  const navigate = useNavigate();

  // Data
  const [txn, setTxn]                   = useState(null);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [loading, setLoading]           = useState(true);

  // UI
  const [selectedEntry, setSelectedEntry] = useState(null);   // { paymentType, mops }
  const [waitingMode, setWaitingMode]     = useState(null);   // null | "qr" | "intent" | "collect" | "card" | "netbanking" | "wallet"
  const paymentPopupRef = useRef(null);   // reference to the popup window (card / NB / wallet)
  const [collectTimer, setCollectTimer]   = useState(600);    // 10 min in seconds
  const [isPolling, setIsPolling]         = useState(false);
  const [sessionLeft, setSessionLeft]     = useState(SESSION_DURATION);

  // ── Session Countdown ──────────────────────────────────────
  useEffect(() => {
    const stored = Number(sessionStorage.getItem(SESSION_KEY));
    const start  = Number.isFinite(stored) && stored > 0 ? stored : Date.now();
    if (!stored || stored <= 0) sessionStorage.setItem(SESSION_KEY, String(start));

    const tick = () => {
      const elapsed = Math.floor((Date.now() - start) / 1000);
      const left    = Math.max(SESSION_DURATION - elapsed, 0);
      setSessionLeft(left);
      if (left === 0) { sessionStorage.removeItem(SESSION_KEY); window.location.reload(); }
    };
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, []);

  // ── Load Payment Details ────────────────────────────────────
  useEffect(() => {
    if (!id) return;
    (async () => {
      setLoading(true);
      try {
        const res = await PaymentService.getPaymentDetailsRaw(id);

        if (res.statusCode === 200) {
          // Normal flow — render payment UI
          setTxn(res.txn);
          const types = res.paymentTypes ?? [];
          setPaymentTypes(types);
          if (types.length > 0) setSelectedEntry(types[0]);

        } else if (res.transaction && res.status === "SUCCESS") {
          // Transaction already paid — skip the payment UI and go straight to result
          const t = res.transaction;
          navigate("/response-page", {
            replace: true,
            state: {
              response: {
                txnId:           t.transactionId,
                orderId:         t.orderId,
                amount:          t.amount,
                status:          t.status,
                utr:             t.utr,
                custEmail:       t.customerEmail,
                custPhone:       t.customerPhone,
                responseMessage: res.message || "Transaction is already Paid",
                returnUrl:       t.returnUrl ?? null,
              },
            },
          });

        } else {
          toast.error(res.message || "Failed to load payment details");
        }
      } catch {
        toast.error("An error occurred while loading payment details");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  // ── Resume Polling After UPI App Redirect ──────────────────
  useEffect(() => {
    if (!txn?.transactionId) return;
    const shouldPoll = sessionStorage.getItem("shouldPollPayment");
    const storedId   = sessionStorage.getItem("pollingOrderId");
    if (shouldPoll === "true" && storedId === txn.transactionId) {
      setIsPolling(true);
      setWaitingMode("intent");
    }
  }, [txn?.transactionId]);

  // ── Polling ─────────────────────────────────────────────────
  useEffect(() => {
    if (!isPolling || !txn?.transactionId) return;

    const transactionId = txn.transactionId;
    const returnUrl     = txn.returnUrl;

    const check = async () => {
      try {
        const res = await PaymentService.getPaymentStatus(transactionId);
        // transactionStatus = actual payment outcome (SUCCESS / FAILED / SENTTOBANK / etc.)
        // status            = API call status — always SUCCESS when the HTTP call works
        const txnStatus = res.transactionStatus;
        if (txnStatus && TERMINAL_STATUSES.includes(txnStatus)) {
          setIsPolling(false);
          setWaitingMode(null);
          sessionStorage.removeItem("shouldPollPayment");
          sessionStorage.removeItem("pollingOrderId");
          // Close the 3DS/bank popup window — we opened it, so we can close it
          if (paymentPopupRef.current && !paymentPopupRef.current.closed) {
            paymentPopupRef.current.close();
            paymentPopupRef.current = null;
          }
          navigate("/response-page", {
            state: {
              response: {
                status:          txnStatus,           // use the transaction-level status
                utr:             res.utr,
                responseMessage: res.responseMessage || res.message,
                txnId:           txn.transactionId,
                orderId:         txn.orderId,
                amount:          txn.amount,
                custEmail:       txn.customerEmail,
                custPhone:       txn.customerPhone,
                returnUrl,
              },
            },
          });
        }
      } catch {
        // silent — keep retrying
      }
    };

    check();
    const interval = setInterval(check, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPolling, txn?.transactionId, navigate]);

  // ── Collect Timer Countdown ──────────────────────────────────
  useEffect(() => {
    if (waitingMode !== "collect") return;
    if (collectTimer <= 0) {
      setIsPolling(false);
      setWaitingMode(null);
      toast.warning("Payment request expired. Please try again.");
      return;
    }
    const timer = setInterval(() => setCollectTimer((t) => t - 1), 1000);
    return () => clearInterval(timer);
  }, [waitingMode, collectTimer]);

  // ── Handle Payment Initiated Callback ───────────────────────
  // `popup` is the window reference returned by openPaymentPopup() — only
  // provided for card / netbanking / wallet flows.
  const handlePaymentInitiated = useCallback((mode, popup = null) => {
    if (mode === "collect") setCollectTimer(600);
    // "qr" → polling only, no overlay (QR stays visible for scanning)
    setWaitingMode(mode === "qr" ? null : mode);
    setIsPolling(true);
    if (popup) paymentPopupRef.current = popup;
  }, []);

  // ── Watch for popup closure ──────────────────────────────────
  // When the payment popup (card / NB / wallet) closes, hide the overlay
  // and let the existing polling loop handle the navigation.
  useEffect(() => {
    if (!paymentPopupRef.current) return;

    const interval = setInterval(() => {
      const popup = paymentPopupRef.current;
      if (!popup || popup.closed) {
        clearInterval(interval);
        paymentPopupRef.current = null;
        // Overlay stays visible — polling will navigate when status arrives.
        // But if polling hasn't started yet (edge case), kick it off.
        setIsPolling((prev) => prev || true);
      }
    }, 800);

    return () => clearInterval(interval);
  }, [waitingMode]);

  // ── Render Active Section ────────────────────────────────────
  const renderSection = () => {
    if (!selectedEntry || !txn) return (
      <Box textAlign="center" sx={{ p: 4, color: "#9ca3af" }}>
        <Typography variant="h6" gutterBottom color="#374151">Secure Checkout</Typography>
        <Typography>Select a payment method on the left to get started.</Typography>
      </Box>
    );

    const { paymentType, mops } = selectedEntry;
    const props = { mops, txn, onPaymentInitiated: handlePaymentInitiated };

    switch (paymentType) {
      case "UP": return <UpiSection {...props} />;
      case "CC":
      case "DC": return <CardSection paymentType={paymentType} {...props} />;
      case "NB": return <NetBankingSection {...props} />;
      case "WL": return <WalletSection {...props} />;
      default:   return null;
    }
  };

  // ── Guards ───────────────────────────────────────────────────
  if (loading) return <Loading />;

  const amount = parseFloat(txn?.amount || "0").toFixed(2);

  // ─────────────────────────────────────────────────────────────
  return (
    <>
      {/* ── Payment Waiting Overlay ──────────────────────────── */}
      {waitingMode && waitingMode !== "qr" && (
        <PaymentWaitingOverlay mode={waitingMode} timer={collectTimer} />
      )}

      {/* ── Session Timer Badge ──────────────────────────────── */}
      <Box sx={{
        position: "fixed", top: 16, right: 16, zIndex: 1200,
        bgcolor: "rgba(255,255,255,0.97)",
        border: "1px solid #e5e7eb",
        borderRadius: 999, px: 2, py: 0.75,
        boxShadow: "0 4px 16px rgba(15,23,42,0.1)",
        display: "flex", alignItems: "center", gap: 1,
      }}>
        <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#6b7280", letterSpacing: "0.5px", textTransform: "uppercase" }}>
          Session
        </Typography>
        <Typography sx={{ fontSize: "0.9rem", fontWeight: 800, color: sessionLeft < 60 ? "#ef4444" : "#15b86d" }}>
          {formatTime(sessionLeft)}
        </Typography>
      </Box>

      {/* ── Main Card Layout ─────────────────────────────────── */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          background: "linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%)",
          p: { xs: 2, md: 3 },
        }}
      >
        <Grid
          container
          sx={{
            maxWidth: { xs: "100%", sm: "96%", md: "1100px" },
            width: "100%",
            minHeight: { xs: "auto", md: "680px" },
            background: "#fff",
            borderRadius: { xs: "16px", md: "24px" },
            boxShadow: "0 4px 24px rgba(15,23,42,0.10)",
            overflow: "hidden",
          }}
        >
          {/* ── Left: Payment Methods ─────────────────────── */}
          <Grid
            item xs={12} md={3.5}
            sx={{
              order: { xs: 2, md: 1 },
              borderRight: { xs: "none", md: "1px solid #e5e7eb" },
              borderTop: { xs: "1px solid #e5e7eb", md: "none" },
            }}
          >
            <Box sx={{ background: "#fafbfc", height: "100%" }}>
              <Typography
                variant="subtitle2"
                sx={{
                  px: 3, pt: 3, pb: 2,
                  fontWeight: 700,
                  color: "#6b7280",
                  letterSpacing: "0.6px",
                  textTransform: "uppercase",
                  fontSize: "0.72rem",
                  borderBottom: "1px solid #e5e7eb",
                }}
              >
                Payment Methods
              </Typography>
              <List sx={{ px: 2, py: 2 }}>
                {paymentTypes.map((entry) => (
                  <PaymentMethodItem
                    key={entry.paymentType}
                    entry={entry}
                    selected={selectedEntry?.paymentType === entry.paymentType}
                    onClick={setSelectedEntry}
                  />
                ))}
              </List>
            </Box>
          </Grid>

          {/* ── Centre: Payment Form ─────────────────────── */}
          <Grid
            item xs={12} md={4.5}
            sx={{
              order: { xs: 1, md: 2 },
              p: { xs: 3, md: 4 },
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              minHeight: { xs: "auto", md: 540 },
            }}
          >
            {renderSection()}
          </Grid>

          {/* ── Right: Order Summary ─────────────────────── */}
          <Grid
            item xs={12} md={4}
            sx={{
              order: { xs: 3, md: 3 },
              borderLeft: { xs: "none", md: "1px solid #e5e7eb" },
              borderTop: { xs: "1px solid #e5e7eb", md: "none" },
            }}
          >
            <Box
              sx={{
                p: { xs: 3, md: 4 },
                background: "#f9fafb",
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* Logo */}
              <Box mb={3}>
                <Box component="img" src="/images/logo.png" alt="logo" sx={{ height: 36, objectFit: "contain" }} />
              </Box>

              {/* Paying To */}
              <Box mb={3}>
                <Typography variant="overline" sx={{ color: "#9ca3af", fontWeight: 600, letterSpacing: "1px", fontSize: "0.68rem" }}>
                  PAYING TO
                </Typography>
                <Typography variant="h6" fontWeight={700} color="#1f2937" mt={0.25}>
                  {txn?.businessName || "—"}
                </Typography>
              </Box>

              {/* Order Details */}
              <Box sx={{ background: "#fff", borderRadius: "12px", p: 2.5, mb: 3 }}>
                {[
                  { label: "Order ID",  value: txn?.orderId },
                  { label: "Customer",  value: txn?.customerName },
                  { label: "Email",     value: txn?.customerEmail },
                  { label: "Product",   value: txn?.productDesc },
                ].filter((r) => r.value).map(({ label, value }) => (
                  <Box key={label} display="flex" justifyContent="space-between" mb={1.5}>
                    <Typography variant="caption" color="#6b7280" fontWeight={500}>{label}</Typography>
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      color="#1f2937"
                      sx={{ ml: 1, textAlign: "right", maxWidth: "60%", wordBreak: "break-word" }}
                    >
                      {value}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Box flexGrow={1} />

              {/* Amount */}
              <Box sx={{ background: "#fff", borderRadius: "12px", p: 2.5 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="body1" fontWeight={600} color="#374151">Total Payable</Typography>
                  <Typography variant="h4" fontWeight={800} color="#1f2937" sx={{ fontSize: { xs: "1.6rem", md: "2rem" } }}>
                    ₹{amount}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" justifyContent="center" gap={0.75} mb={2}>
                  <LockIcon sx={{ fontSize: "0.85rem", color: "#9ca3af" }} />
                  <Typography variant="caption" color="#6b7280">
                    Secured by GREEN HITECH VENTURES PVT LTD
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="center">
                  <Box component="img" src="/images/pci-dss.png" alt="PCI DSS" sx={{ height: 20, filter: "grayscale(1)", opacity: 0.5 }} />
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default CheckoutPage;
